import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogIn, UserPlus, LayoutDashboard, LogOut, MessageSquare, Menu, X, ShieldCheck, LifeBuoy } from "lucide-react";
import { NotificationsBell } from "@/components/NotificationsBell";

const LINKS = [["/","Home","home"],["/artisti","Artisti","artists"],["/fornitori","Fornitori","suppliers"],["/consigliati","Consigliati","recommended"],["/store","Store","store"],["/faq","FAQ","faq"]];

export default function Navbar() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(0);
  const userId = user?.id;
  useEffect(() => {
    if (!userId) { setPending(0); return; }
    const load = () => api.get("/bookings/pending-count").then(r=>setPending(r.data.count)).catch(()=>{});
    load();
    const t = setInterval(load, 60000);
    window.addEventListener("bookings-changed", load);
    return () => { clearInterval(t); window.removeEventListener("bookings-changed", load); };
  }, [userId]);
  const badge = pending>0 && <span data-testid="nav-pending-badge" className="ml-1 min-w-5 h-5 px-1.5 rounded-full bg-gold text-navy text-[11px] font-bold inline-flex items-center justify-center">{pending}</span>;
  return (
    <nav className="glass-nav sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src="/logo-icon.png" alt="Artisti in Comune" className="w-12 h-12 object-contain"/>
          <div className="leading-tight">
            <div className="font-logo text-xl font-bold text-aic-navy">Artisti <span className="text-gold">in</span> Comune</div>
            <div className="overline text-aic-navy/60 opacity-70 hidden sm:block lg:hidden xl:block whitespace-nowrap">Portale Istituzionale B2B/B2G/B2C</div>
          </div>
        </Link>
        <div className="hidden lg:flex items-center gap-4 xl:gap-6 text-sm font-medium">
          {LINKS.map(([to,label,id])=>(
            <Link key={to} to={to} data-testid={`nav-${id}`} className="hover:text-[#C98A3C] transition-colors whitespace-nowrap">{label}</Link>
          ))}
        </div>
        <div className="hidden lg:flex items-center gap-2 xl:gap-3 shrink-0 ml-4">
          {user ? (
            <>
              {user.role==="admin" && <Link to="/admin" data-testid="nav-admin" className="btn-pill-gold text-sm"><ShieldCheck className="w-4 h-4"/>Admin</Link>}
              <NotificationsBell/>
              <Link to="/chat" data-testid="nav-chat" className="btn-pill-outline text-sm"><MessageSquare className="w-4 h-4"/>Chat</Link>
              <Link to="/dashboard" data-testid="nav-dashboard" className="btn-pill-outline text-sm"><LayoutDashboard className="w-4 h-4"/>Dashboard{badge}</Link>
              <button data-testid="nav-logout" onClick={() => { logout(); nav("/"); }} className="btn-pill-outline text-sm"><LogOut className="w-4 h-4"/>Esci</button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="btn-pill-outline text-sm"><LogIn className="w-4 h-4"/>Accedi</Link>
              <Link to="/registrati" data-testid="nav-register" className="btn-pill-gold text-sm"><UserPlus className="w-4 h-4"/>Iscriviti</Link>
            </>
          )}
        </div>
        <button data-testid="nav-mobile-toggle" onClick={()=>setOpen(!open)} className="lg:hidden p-2 rounded-full border border-[#0D2B5A]/30" aria-label="Menu">
          {open ? <X className="w-5 h-5"/> : <Menu className="w-5 h-5"/>}
        </button>
      </div>
      {open && (
        <div className="lg:hidden border-t border-[#0D2B5A]/10 bg-white px-6 py-4 space-y-1" onClick={()=>setOpen(false)}>
          {LINKS.map(([to,label,id])=>(
            <Link key={to} to={to} data-testid={`mnav-${id}`} className="block py-2 font-medium hover:text-gold">{label}</Link>
          ))}
          <div className="pt-3 flex flex-wrap gap-2">
            {user ? (
              <>
                {user.role==="admin" && <Link to="/admin" data-testid="mnav-admin" className="btn-pill-gold text-sm"><ShieldCheck className="w-4 h-4"/>Admin</Link>}
                <Link to="/chat" className="btn-pill-outline text-sm"><MessageSquare className="w-4 h-4"/>Chat</Link>
                <Link to="/dashboard" data-testid="mnav-dashboard" className="btn-pill-outline text-sm"><LayoutDashboard className="w-4 h-4"/>Dashboard{badge}</Link>
                {user.role!=="admin" && <Link to="/dashboard?aiuto=1" data-testid="mnav-help" className="btn-pill-outline text-sm"><LifeBuoy className="w-4 h-4"/>Aiuto</Link>}
                <button data-testid="mnav-logout" onClick={() => { logout(); nav("/"); }} className="btn-pill-outline text-sm"><LogOut className="w-4 h-4"/>Esci</button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="mnav-login" className="btn-pill-outline text-sm"><LogIn className="w-4 h-4"/>Accedi</Link>
                <Link to="/registrati" data-testid="mnav-register" className="btn-pill-gold text-sm"><UserPlus className="w-4 h-4"/>Iscriviti</Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
